/* eslint-disable jsx-a11y/alt-text */
import './css/PublicFiguresPage.css';

import { observer } from 'mobx-react-lite';
import { Suspense, useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';

import { Virtuoso } from 'react-virtuoso';
import {
  IonContent,
  IonIcon,
  IonListHeader,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  RefresherEventDetail,
} from '@ionic/react';

import _ from 'lodash';
import { LoadingFallback } from '../../App';
import { useStores } from '../../models';
import { RunModel } from '../../models/RunModel/RunModel';
import RunListItem from '../../components/listElements/RunListItem/RunListItem';
import EmptyTrickList from './components/EmptyList';
import FavoritesCarousel from './components/FavoritesCarousel';
import { Trans } from 'react-i18next';
import ViewRun from '../../components/ViewRun';
import VideoPlayerView from '../ViewRunPage/VideoPlayerView';
import { RunEditContextProvider } from '../ViewRunPage/index';
import { IonButton } from '@ionic/react';

const PublicRuns: React.FC = observer(() => {
  const { recordList } = useStores();
  const { id: selectedRunId } = useParams<{ id?: string }>();
  const history = useHistory();

  // Check if running as web app
  const isWeb = process.env.REACT_APP_IS_WEB === 'true';
  
  // State for selected run and video player when in web mode
  const [videoActive, setVideoActive] = useState(false);

  // Find selected run from public runs
  const selectedRun = selectedRunId 
    ? recordList.publicRuns.find(run => run.id === selectedRunId)
    : null;

  // Create edit context for ViewRun components
  const editContext = {
    editMode: false,
    setEditMode: () => {},
    record: selectedRun,
    videoActive,
    setVideoActive,
    hasVideo: !!selectedRun?.localVideoUri,
    isEdit: false,
    onSectionRename: () => {},
    startEditMode: () => {},
  };

  // Handle run click for web mode
  const handleRunClick = (run: RunModel) => {
    if (isWeb) {
      history.push(`/public/${run.id}`);
    }
  };

  // Toggle between video and 3D view
  const toggleVideoView = () => {
    setVideoActive(!videoActive);
  };

  // Whenever we enter the page, we want to query for new public items.
  // We also want to do this when pulling down at the top of the list (the reload gesture) so we register the handlePullDown trigger
  useEffect(() => {
    recordList.refreshPublicRuns();
  }, [recordList]);

  // Auto-select second run on first load for web app
  useEffect(() => {
    if (isWeb && !selectedRunId && recordList.publicRuns.length >= 2) {
      const secondRun = recordList.publicRuns[1];
      if (secondRun) {
        history.replace(`/public/${secondRun.id}`);
      }
    }
  }, [isWeb, selectedRunId, recordList.publicRuns, history]);

  function handlePullDown(event: CustomEvent<RefresherEventDetail>) {
    recordList.refreshPublicRuns().finally(() => {
      event.detail.complete();
    });
  }

  function handleScrolledToEnd() {
    recordList.loadMorePublicRuns().finally(() => {});
  }

  const favorites: Array<RunModel> = [
    ...recordList.publicFavorites,
    ...recordList.friendFavorites,
  ];

  return (
    <Suspense fallback={<LoadingFallback />}>
      <IonPage className={`notchSafe ${isWeb ? 'web-layout' : ''}`}>
        <IonContent scrollY={false}>
          {isWeb ? (
            // Web layout: Two-column with 3D view
            <div className="public-runs-main-container">
              {/* Left column for runs list */}
              <div className="public-runs-left-column">
                <IonRefresher onIonRefresh={handlePullDown}>
                  <IonRefresherContent></IonRefresherContent>
                </IonRefresher>
                
                <div className="favorites-carousel-wrapper">
                  <FavoritesCarousel
                    favorites={favorites}
                    onClick={isWeb ? handleRunClick : undefined}
                  />
                </div>

                <div className="section-container">
                  <div className="public-pastruns-header">
                    <IonListHeader className="section-header">
                      <IonIcon
                        src={`/assets/icons/TrickSpikeIcon.svg`}
                        style={{ width: '0.85em', marginLeft: '8px' }}
                      />
                      <Trans i18nKey="PUBLIC_RUNS">Runs</Trans>

                      <span
                        className="section-count"
                        style={{ color: 'var(--ion-color-primary)' }}
                      >
                        ({recordList.publicRuns.length})
                      </span>
                    </IonListHeader>
                  </div>
                </div>

                {/* Show empty state when no content is available */}
                {favorites.length + recordList.publicRuns.length === 0 && (
                  <EmptyTrickList />
                )}
                
                <Virtuoso
                  totalCount={recordList.publicRuns.length}
                  endReached={() => handleScrolledToEnd()}
                  itemContent={(index) => {
                    const item = recordList.publicRuns[index];
                    return (
                      <div
                        className={`public-runlist-item-container ${
                          isWeb && selectedRunId === item.id ? 'selected' : ''
                        }`}
                      >
                        <div className="public-runlist-item-inner">
                          <RunListItem
                            key={`public_${item.id}_${index}`}
                            item={item}
                            isPublic={true}
                            isFriend={true}
                            directNavigation={!isWeb}
                            onClick={isWeb ? handleRunClick : undefined}
                          />
                        </div>
                      </div>
                    );
                  }}
                />
              </div>

              {/* Right column for 3D view */}
              <div className="public-runs-right-column">
                {selectedRun ? (
                  <div
                    style={{
                      width: '100%',
                      height: '100%',
                      position: 'relative',
                    }}
                  >
                    <div style={{ width: '100%', height: '100%' }}>
                      <RunEditContextProvider value={editContext}>
                        <VideoPlayerView
                          videoActive={videoActive}
                          onVideoToggle={toggleVideoView}
                          hasVideo={!!selectedRun.localVideoUri}
                          record={selectedRun}
                          hidden={!videoActive}
                          showRunStatsBox={true}
                          showFigurePopup={false}
                          showGForceDisplay={false}
                        />
                        <ViewRun
                          record={selectedRun}
                          hidden={videoActive}
                          videoActive={videoActive}
                          onVideoToggle={toggleVideoView}
                          hasVideo={!!selectedRun.localVideoUri}
                          showRunStatsBox={true}
                          showFigurePopup={false}
                          showGForceDisplay={false}
                        />
                      </RunEditContextProvider>
                    </div>
                  </div>
                ) : (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                      backgroundColor: 'var(--ion-color-primary)',
                      color: 'var(--ion-color-primary-contrast)',
                      fontSize: '18px',
                      fontWeight: '500',
                    }}
                  >
                    Select a run to view details
                  </div>
                )}
              </div>
            </div>
          ) : (
            // Mobile layout: Single column
            <>
              <IonRefresher slot="fixed" onIonRefresh={handlePullDown}>
                <IonRefresherContent></IonRefresherContent>
              </IonRefresher>

              <div className="favorites-carousel-wrapper">
                <FavoritesCarousel favorites={favorites} />
              </div>

              <div className="section-container">
                <div className="public-pastruns-header">
                  <IonListHeader className="section-header">
                    <IonIcon
                      src={`/assets/icons/TrickSpikeIcon.svg`}
                      style={{ width: '0.85em', marginLeft: '8px' }}
                    />
                    <Trans i18nKey="PUBLIC_RUNS">Runs</Trans>

                    <span
                      className="section-count"
                      style={{ color: 'var(--ion-color-primary)' }}
                    >
                      ({recordList.publicRuns.length})
                    </span>
                  </IonListHeader>
                </div>
              </div>

              {/* Show empty state when no content is available */}
              {favorites.length + recordList.publicRuns.length === 0 && (
                <EmptyTrickList />
              )}
              
              <Virtuoso
                totalCount={recordList.publicRuns.length}
                endReached={() => handleScrolledToEnd()}
                itemContent={(index) => {
                  const item = recordList.publicRuns[index];
                  return (
                    <div className="public-runlist-item-container">
                      <div className="public-runlist-item-inner">
                        <RunListItem
                          key={`public_${item.id}_${index}`}
                          item={item}
                          isPublic={true}
                          isFriend={true}
                        />
                      </div>
                    </div>
                  );
                }}
              />
            </>
          )}
        </IonContent>
      </IonPage>
    </Suspense>
  );
});

export default PublicRuns;
